import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Seo } from "@/components/Seo";
import { api, API } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [token, setToken] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setToken(data.download_token || null);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      if (tries++ < 10) timer = setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, attempt]);

  const retry = () => { setState("checking"); setAttempt((a) => a + 1); };

  return (
    <div className="min-h-screen bg-cream text-olive-deep">
      <Seo title="Takk for kjøpet – Helserebellen" description="Bekreftelse på kjøpet ditt." path="/payment/success" noindex />
      <Nav />
      <div className="min-h-screen grid place-items-center px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-lg">
          {state === "checking" && (
            <>
              <Loader2 size={44} className="mx-auto text-terra animate-spin" />
              <h1 className="mt-6 font-serif text-3xl">Bekrefter betalingen…</h1>
              <p className="mt-2 text-olive/60">Et lite øyeblikk.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 size={54} className="mx-auto text-sage-deep" />
              <p className="mt-6 font-script text-4xl text-terra">Takk for kjøpet!</p>
              <h1 className="mt-2 font-serif text-4xl">Planen din er klar.</h1>
              <p className="mt-3 text-olive/70">Vi har også sendt nedlastingslenken til e-posten din. Last ned PDF-en din nedenfor.</p>
              {token ? (
                <a href={`${API}/download/${token}`} data-testid="download-plan"
                  className="mt-8 inline-flex items-center gap-2 rounded-md bg-terra px-9 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep transition-colors">
                  <Download size={18} /> Last ned planen
                </a>
              ) : (
                <p className="mt-8 text-olive/60 text-sm" data-testid="download-email-only">
                  Nedlastingslenken er sendt til e-posten din. Sjekk innboksen (og spam).
                </p>
              )}
              <div className="mt-6">
                <Link to="/" className="inline-flex items-center gap-2 text-olive/60 text-sm hover:text-terra"><ArrowLeft size={16} /> Til forsiden</Link>
              </div>
            </>
          )}
          {state === "failed" && (
            <>
              <h1 className="font-serif text-3xl">Vi fant ikke betalingen ennå.</h1>
              <p className="mt-3 text-olive/70">Noen ganger tar bekreftelsen litt tid. Prøv igjen, eller ta kontakt hvis du ble belastet – så ordner vi det med en gang.</p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <button onClick={retry} data-testid="payment-retry"
                  className="inline-flex items-center gap-2 rounded-md bg-terra px-8 py-4 text-cream text-[13px] font-semibold uppercase tracking-[0.12em] hover:bg-olive-deep transition-colors">
                  Prøv igjen
                </button>
                <Link to="/#velg" className="inline-flex items-center gap-2 rounded-md border border-olive/40 px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.12em] text-olive-deep hover:bg-olive-deep hover:text-cream transition-colors">
                  Tilbake til planene
                </Link>
              </div>
              {sessionId && (
                <p className="mt-6 text-[11px] text-olive/40" data-testid="payment-ref">Referanse: {sessionId}</p>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
